
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Send, UserRound, UserCog, Sparkles, Zap } from 'lucide-react';
import TextareaAutosize from 'react-textarea-autosize';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { MessageOptions } from '@/types/openai';
import { motion, AnimatePresence } from 'framer-motion';

interface MessageInputProps {
  onSendMessage: (options: MessageOptions) => void;
  onUsageCheck: (topic: 'guide' | 'performance' | 'trading_strategy' | 'advisor') => Promise<boolean>;
  isDisabled?: boolean;
  topic: 'guide' | 'performance' | 'trading_strategy' | 'advisor';
  isExpertMode: boolean;
  onModeToggle: () => void;
}

// Smart suggestions based on topic and context
const getSmartSuggestions = (topic: string, message: string, t: any) => {
  const messageLower = message.toLowerCase();
  
  const suggestions: Record<string, string[]> = {
    guide: [
      t('ai_assistant.smart_suggestions.guide.getting_started'),
      t('ai_assistant.smart_suggestions.guide.best_practices'),
      t('ai_assistant.smart_suggestions.guide.step_by_step'),
    ],
    performance: [
      t('ai_assistant.smart_suggestions.performance.latest_metrics'),
      t('ai_assistant.smart_suggestions.performance.improve_performance'),
      t('ai_assistant.smart_suggestions.performance.key_indicators'),
    ],
    trading_strategy: [
      t('ai_assistant.smart_suggestions.trading_strategy.best_strategy'),
      t('ai_assistant.smart_suggestions.trading_strategy.manage_risk'),
      t('ai_assistant.smart_suggestions.trading_strategy.analyze_trade'),
    ],
    advisor: [
      t('ai_assistant.smart_suggestions.advisor.recommendation'),
      t('ai_assistant.smart_suggestions.advisor.invest_now'),
      t('ai_assistant.smart_suggestions.advisor.market_trends'),
    ],
  };

  // Filter suggestions based on current input
  const topicSuggestions = suggestions[topic] || suggestions.guide;
  
  if (messageLower.length < 3) {
    return topicSuggestions.slice(0, 2);
  }
  
  return topicSuggestions.filter(suggestion => 
    !suggestion.toLowerCase().includes(messageLower) &&
    messageLower.split(' ').some(word => 
      word.length > 2 && suggestion.toLowerCase().includes(word)
    )
  ).slice(0, 2);
};

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onUsageCheck,
  isDisabled = false,
  topic,
  isExpertMode,
  onModeToggle
}) => {
  
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();

  const getAccentColor = () => {
    switch (topic) {
      case 'guide':
        return 'botscheme-royal-purple';
      case 'performance':
        return 'botscheme-ocean-blue';
      case 'trading_strategy':
        return 'botscheme-emerald-green';
      case 'advisor':
        return 'botscheme-amber-gold';
      default:
        return 'primary';
    }
  };

  const accentColor = getAccentColor();
  
  // Smart suggestions based on current input
  const smartSuggestions = getSmartSuggestions(topic, message, t);
  
  // Handle input changes with smart suggestions
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMessage(value);
    setIsTyping(value.length > 0);
    setShowSuggestions(value.length > 2 && value.length < 50);
  }, []);

  // Handle focus state
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  }, []);
  
  // Handle sending message when pressing Enter (but not with Shift)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) =>  {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
      
      // Handle suggestion selection with arrow keys
      if (e.key === 'Tab' && showSuggestions && smartSuggestions.length > 0) {
        e.preventDefault();
        setMessage(smartSuggestions[0]);
        setShowSuggestions(false);
      }
    },
    [message, showSuggestions, smartSuggestions] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSendMessage = useCallback(async () => {
    if (message.trim() && !isDisabled) {
      try {
        // STEP 1: Check usage limits BEFORE sending
        const canSend = await onUsageCheck(topic);
        if (!canSend) {
          // Usage check will show appropriate error message
          return;
        }

        // STEP 2: If usage check passed, proceed to send message
        onSendMessage({
          content: message.trim(),
          includeMultiLanguage: true,
          mode: isExpertMode ? 'expert' : 'conversation',
          stream: true // Enable streaming by default
        });
        
        setMessage('');
        setIsTyping(false);
        setShowSuggestions(false);
        
        // Focus back on textarea after sending
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 10);
      } catch (error) {
        console.error('[MESSAGE INPUT] Error in handleSendMessage:', error);
      }
    }
  }, [message, onSendMessage, onUsageCheck, topic, isDisabled, isExpertMode]);

  const handleSuggestionClick = useCallback((suggestion: string) => {
    setMessage(suggestion);
    setShowSuggestions(false);
    textareaRef.current?.focus();
  }, []);

  // Focus on textarea when component mounts
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  return (
    <div className="relative w-full">
      {/* Smart suggestions dropdown */}
      <AnimatePresence>
        {showSuggestions && smartSuggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute bottom-full left-0 right-0 mb-2 z-50"
          >
            <div className={cn(
              "bg-background/95 backdrop-blur-md border border-border/50 rounded-xl shadow-xl",
              "max-h-32 overflow-y-auto"
            )}>
              {smartSuggestions.map((suggestion, index) => (
                <motion.button
                  key={suggestion}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm hover:bg-accent/50 transition-colors",
                    "first:rounded-t-xl last:rounded-b-xl",
                    `text-${accentColor}/80 hover:text-${accentColor}`
                  )}
                  onClick={() => handleSuggestionClick(suggestion)}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 4 }}
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-3 w-3 opacity-60" />
                    <span>{suggestion}</span>
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main input container - Optimized for mobile */}
      <motion.div 
        className={cn(
          "relative bg-background rounded-2xl",
          "transition-all duration-300 ease-out",
          // Full width within parent constraint
          "w-full",
          // Enhanced shadow effects without border
          "shadow-md hover:shadow-lg",
          // Enhanced focus states with subtle shadow glow
          isFocused && [
            "shadow-lg",
            "ring-1 ring-border/20"
          ]
        )}
        animate={{
          scale: isFocused ? 1.002 : 1,
        }}
        transition={{ duration: 0.2 }}
      >
        {/* Single row layout with textarea and buttons - Optimized padding */}
        <div className={cn(
          "flex items-end gap-2",
          // Reduced padding for mobile
          isMobile ? "p-2" : "p-3"
        )}>
          {/* Mode toggle button - Smaller on mobile */}
          <motion.div 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }}
            className="flex-shrink-0"
          >
            <Button 
              onClick={onModeToggle}
              size="icon"
              variant="ghost"
              className={cn(
                "rounded-full transition-all duration-200",
                "hover:bg-accent/50",
                // Smaller buttons on mobile
                isMobile ? "h-8 w-8" : "h-10 w-10",
                isExpertMode ? [
                  `text-${accentColor} bg-${accentColor}/10`,
                  "shadow-sm"
                ] : "text-muted-foreground"
              )}
              disabled={isDisabled}
              type="button"
              title={isExpertMode ? t('ai_assistant.switch_to_conversation_mode') : t('ai_assistant.switch_to_expert_mode')}
              aria-label={isExpertMode ? t('ai_assistant.switch_to_conversation_mode') : t('ai_assistant.switch_to_expert_mode')}
            >
              <motion.div
                animate={{ rotate: isExpertMode ? 360 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {isExpertMode ? (
                  <UserCog className={isMobile ? "h-3.5 w-3.5" : "h-4 w-4"} />
                ) : (
                  <UserRound className={isMobile ? "h-3.5 w-3.5" : "h-4 w-4"} />
                )}
              </motion.div>
            </Button>
          </motion.div>
          
          {/* Textarea container */}
          <div className="flex-1 min-w-0">
            <TextareaAutosize
              ref={textareaRef}
              className={cn(
                "w-full resize-none bg-transparent outline-none border-none",
                "text-sm placeholder:text-muted-foreground/70",
                // Optimized padding and line height
                "leading-5 py-1.5",
                "transition-all duration-200"
              )}
              placeholder={t('ai_assistant.type_message')}
              value={message}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={isDisabled}
              rows={1}
              maxRows={isMobile ? 3 : 4}
              style={{
                // Responsive font size to prevent zoom on iOS
                fontSize: isMobile ? '16px' : '14px',
              }}
            />
          </div>
          
          {/* Send button - Smaller on mobile */}
          <motion.div 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }}
            className="flex-shrink-0"
          >
            <Button 
              onClick={handleSendMessage}
              size="icon"
              className={cn(
                "rounded-full transition-all duration-200",
                `bg-${accentColor}/90 hover:bg-${accentColor}`,
                "shadow-sm hover:shadow-md",
                // Smaller buttons on mobile
                isMobile ? "h-8 w-8" : "h-10 w-10",
                // Enhanced disabled state
                (message.trim() === '' || isDisabled) && "opacity-50 cursor-not-allowed"
              )}
              disabled={message.trim() === '' || isDisabled}
              type="submit"
              aria-label={t('ai_assistant.send_message')}
            >
              <motion.div
                animate={isTyping && !isDisabled ? { 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1] 
                } : {}}
                transition={{ 
                  duration: 0.5, 
                  repeat: (isTyping && !isDisabled) ? Infinity : 0,
                  repeatDelay: 2 
                }}
              >
                {isTyping && !isDisabled ? (
                  <Zap className={isMobile ? "h-3.5 w-3.5" : "h-4 w-4"} />
                ) : (
                  <Send className={isMobile ? "h-3.5 w-3.5" : "h-4 w-4"} />
                )}
              </motion.div>
            </Button>
          </motion.div>
        </div>
      </motion.div>
      
      {/* Smart input hint */}
      <AnimatePresence>
        {showSuggestions && smartSuggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute -bottom-6 left-4 text-xs text-muted-foreground/60"
          >
            {t('ai_assistant.press_tab_hint')}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
